/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import * as React from 'react';
import { Box, Text } from 'grommet';
import { ImageExtensions, UserCredentialType } from 'blb-constants/ron';
import ReactSVG from 'react-svg';
import { PDFViewer } from 'blb-components/AuthenticatedApp/PDFViewer/PDFViewer';
import { AlertBar } from 'blb-components/GrommetComponents';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { DigitalCertificateMeta } from 'blb-ui-types/ronMeta';
import { ImageViewer } from 'blb-components/AuthenticatedApp/RON/CredentialViewer/ImageViewer';
import { formatCredentialDate } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/transformCredentialMeta';
import { DATE_ISO_FORMAT, DATE_US_SHORT_FORMAT } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/dateUtils';

export const getFileExtension = (name?: string): string => {
  if (!name) {
    return '';
  }
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex < 0) {
    return '';
  }
  return name.substring(dotIndex).toLowerCase();
};

export namespace CredentialViewer {
  export interface Props {
    certificateMetaData?: DigitalCertificateMeta;
    credentialType: UserCredentialType;
    error?: string;
    file?: File;
    isLoading: boolean;
    uploadDocumentLink?: React.ReactElement;
  }
}

const CertificateMetaDataLine: React.FC<{ field: string; value?: string }> = ({ field, value }) => {
  return (
    <Box direction="row" id={`it-certificate${field}Text`} justify="between">
      <dt>
        <Text size="small" weight="bold">
          {field}:
        </Text>
      </dt>
      <dd>
        <Text size="small">{value}</Text>
      </dd>
    </Box>
  );
};

/** This component renders meta data for one credential as a card */
export const CredentialViewer: React.FC<CredentialViewer.Props> = ({
  uploadDocumentLink,
  credentialType,
  certificateMetaData,
  file,
  isLoading,
  error,
}) => {
  if (error) {
    return <AlertBar errorMessage={error} id="it-error" />;
  }

  if (isLoading) {
    return <CenteredLoadingSpinner />;
  }

  if (credentialType === UserCredentialType.DIGITAL_CERTIFICATE && certificateMetaData) {
    return (
      <Box align="center" direction="column" flex="grow" gap="small" justify="center">
        <Box align="stretch">
          <Box align="center" id="it-uploadedIcon">
            <ReactSVG path={'./assets/paper-stack.svg'} />
          </Box>
          <Box margin={{ bottom: 'small' }}>
            <Text weight="bold">We pulled the following from your digital certificate:</Text>
          </Box>
          <dl>
            <CertificateMetaDataLine field="Issuer" value={certificateMetaData.issuer} />
            <CertificateMetaDataLine field="Subject" value={certificateMetaData.subject} />
            <CertificateMetaDataLine
              field="Expiration"
              value={formatCredentialDate(certificateMetaData.expirationDate, DATE_ISO_FORMAT, DATE_US_SHORT_FORMAT)}
            />
          </dl>
        </Box>
      </Box>
    );
  } else if (credentialType !== UserCredentialType.DIGITAL_CERTIFICATE && file) {
    if (ImageExtensions.includes(getFileExtension(file.name))) {
      return <ImageViewer alt="Credential file" blob={file} />;
    } else {
      return <PDFViewer file={file} />;
    }
  } else {
    // render upload link when file is missing
    return (
      <Box flex="grow" justify="center">
        <Box align="center" gap="xxsmall">
          <ReactSVG path={'./assets/folder-cloud-upload.svg'} />
          <Text size="medium">No file uploaded</Text>
          {uploadDocumentLink}
        </Box>
      </Box>
    );
  }
};
